import { typedAction } from "../helpers";
import { SET_TENANT_INFO } from "./actionTypes";
import { TenantState } from "../models/reduxModels";
import { logger } from "../../../../utilities/logger";
import { ITenantInfo } from "../../interfaces/ITenantInfo";
import { apiGetTenantInfo } from "../../services/ApiService";

const initialState: TenantState = {
  tenantId: "",
  friendlyName: "",
  appTitle: "",
  appLogo: "",
  appColor: "",
  allowedAdvancedSearchFields: [],
  allowedSiteCollections: [],
  allowedTeams: [],
  hiddenMetadataFields: [],
  datasources: [],
  sites: [],
  metadataTextColors: []
};

export const getTenantInfo = async (tenantId: string) => {
  try {
    const tenantInfo: ITenantInfo = await apiGetTenantInfo(tenantId);
    if (tenantInfo) {
      const allowedAdvancedSearchFields = tenantInfo.allowedAdvancedSearchFields?.toLowerCase()
        .split(";")
        .map(allowedField => allowedField.trim())
        .filter(allowedField => allowedField.length > 0);

      const allowedSiteCollections = tenantInfo.allowedSiteCollections?.toLowerCase()
        .split(";")
        .map(allowedSite => allowedSite.trim())
        .filter(allowedSite => allowedSite.length > 0);

      const allowedTeams = tenantInfo.allowedTeams?.toLowerCase()
        .split(";")
        .map(allowedTeam => allowedTeam.trim())
        .filter(allowedTeam => allowedTeam.length > 0);

      const hiddenMetadataFields = tenantInfo.hiddenMetadataFields?.toLowerCase()
        .split(";")
        .map(hiddenField => hiddenField.trim())
        .filter(hiddenField => hiddenField.length > 0);

      const tenantState: TenantState = {
        tenantId: tenantInfo.tenantId,
        friendlyName: tenantInfo.friendlyName,
        appColor: tenantInfo.appColor,
        appLogo: tenantInfo.appLogo,
        appTitle: tenantInfo.appTitle,
        allowedAdvancedSearchFields: allowedAdvancedSearchFields,
        allowedSiteCollections: allowedSiteCollections,
        allowedTeams: allowedTeams,
        hiddenMetadataFields: hiddenMetadataFields,
        datasources: tenantInfo.datasources,
        sites: tenantInfo.sites,
        enablePII: tenantInfo.enablePII,
        tenantEnableApprovalHooks: tenantInfo.tenantEnableApprovalHooks,
        enableRedaction: tenantInfo.enableRedaction,
        replaceFileNameWithGUID: tenantInfo.replaceFileNameWithGUID,
        disableFileRename: tenantInfo.disableFileRename,
        enablePermissionManagement: tenantInfo.enablePermissionManagement,
        openDocumentsInClientApp: tenantInfo.openDocumentsInClientApp,
        enableCopyMoveTo: tenantInfo.enableCopyMoveTo,
        enableSharing: tenantInfo.enableSharing,
        enableHix: tenantInfo.enableHix,
        templateAiEnabled: tenantInfo.templateAiEnabled ?? false,
        enableNewTemplateFilters: tenantInfo.enableNewTemplateFilters,
        newTemplateLibraryFilters: tenantInfo.newTemplateLibraryFilters,
        newTemplateFolderFilters: tenantInfo.newTemplateFolderFilters,
        newTemplateDocumentSetFilters: tenantInfo.newTemplateDocumentSetFilters,
        nintexClientId: tenantInfo.nintexClientId,
        nintexClientSecret: tenantInfo.nintexClientSecret,
        minVersionLimit: tenantInfo.minVersionLimit,
        autoDeleteMinorVersionsOnPublish: tenantInfo.autoDeleteMinorVersionsOnPublish,
        requiredMetadataOnPublish: tenantInfo.requiredMetadataOnPublish,
        enableExchange: tenantInfo.enableExchange,
        emailToMetadataMapping: tenantInfo.emailToMetadataMapping,
        hideAutoPreview: tenantInfo.hideAutoPreview,
        hideDocumentLibrariesInTeams: tenantInfo.hideDocumentLibrariesInTeams,
        hideTeamsSitesInSharePoint: tenantInfo.hideTeamsSitesInSharePoint,
        defaultChannelTabLocation: tenantInfo.defaultChannelTabLocation,
        metadataTextColors: tenantInfo.metadataTextColors,
        isTessEnabled: tenantInfo?.isTessEnabled,
        clientListUrl: tenantInfo.clientListUrl,
        disableMailCategorization: tenantInfo.disableMailCategorization,
        enableAdvSearchMultiSites: tenantInfo.enableAdvSearchMultiSites,
        disableAdvSearchContentTypes: tenantInfo.disableAdvSearchContentTypes,
        enforceStrictDocumentSetsOnly: tenantInfo.enforceStrictDocumentSetsOnly,
        disableAutoOpenAfterNewFileFromTemplate: tenantInfo.disableAutoOpenAfterNewFileFromTemplate,
        enableProvisionedSiteRegistration: tenantInfo.enableProvisionedSiteRegistration,
        docubirdManagedSitesDatasources: tenantInfo.docubirdManagedSitesDatasources,
        enableQuickFilter: tenantInfo.enableQuickFilter,
        hideTitleFieldFilter: tenantInfo.hideTitleFieldFilter,
        autoOpenFilterSearchbox: tenantInfo.autoOpenFilterSearchbox
      };

      return tenantState;
    }
  } catch (error) {
    logger(error, "tenant.ts (getTenantInfo)");
  }

  return initialState;
};

export const setTenantInfo = (tenantInfo: TenantState) => {
  return typedAction(SET_TENANT_INFO, tenantInfo);
};

type TenantAction = ReturnType<typeof setTenantInfo>;

export function tenantReducer(state = initialState, action: TenantAction): TenantState {
  switch (action.type) {
    case SET_TENANT_INFO:
      return action.payload;
    default:
      return state;
  }
}
